import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Smartphone, BarChart3, Users, Link2 } from 'lucide-react';
import { Button } from '@/components';
import logo from '@/assets/images/logo.png';
import logoWhite from '@/assets/images/logo-white.png';

interface AuthLayoutProps {
  children: React.ReactNode;
}

const productSlides = [
  {
    title: 'Automate Your Social Media',
    description: 'Schedule and manage all your social media posts from one centralized dashboard. Save time and maintain consistency across all platforms.',
    icon: Smartphone,
  },
  {
    title: 'Track Performance',
    description: 'Get detailed analytics and insights on your social media performance. Understand what works and optimize your strategy.',
    icon: BarChart3,
  },
  {
    title: 'Team Collaboration',
    description: 'Work together with your team. Assign tasks, review content, and maintain brand consistency across all channels.',
    icon: Users,
  },
  {
    title: 'Multi-Platform Support',
    description: 'Connect and manage multiple social media accounts including Instagram, Twitter, Facebook, LinkedIn, and more.',
    icon: Link2,
  },
];

export const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % productSlides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % productSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + productSlides.length) % productSlides.length);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Product Info Slider (hidden on mobile, 50% on large screens) */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{ backgroundColor: '#5E46BF' }}
      >
        {/* Logo on left side */}
        <div className="absolute top-8 left-8 z-10">
          <Link to="/login" className="inline-block">
            <img 
              src={logoWhite} 
              alt="Postpally" 
              className="h-8 w-auto cursor-pointer hover:opacity-80 transition-opacity"
            />
          </Link>
        </div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="w-full max-w-md relative">
            {/* Slide Container */}
            <div className="relative h-full min-h-[400px]">
              {productSlides.map((slide, index) => {
                const IconComponent = slide.icon;
                return (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-500 ${
                      index === currentSlide ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <div className="flex flex-col items-center justify-center h-full text-center space-y-6">
                      <div className="mb-4 p-4 rounded-full bg-white/10 backdrop-blur-sm">
                        <IconComponent className="h-12 w-12 text-white" />
                      </div>
                      <h2 className="text-3xl font-bold text-white">
                        {slide.title}
                      </h2>
                      <p className="text-lg text-white/90 leading-relaxed">
                        {slide.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Navigation Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {productSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentSlide
                      ? 'w-8 bg-white'
                      : 'w-2 bg-white/30'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            {/* Navigation Arrows */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 text-white hover:bg-white/10"
              onClick={prevSlide}
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 text-white hover:bg-white/10"
              onClick={nextSlide}
              aria-label="Next slide"
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </div>

      {/* Right Side - Auth Forms (100% on mobile, 50% on large screens) */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-4 sm:p-6 lg:p-12 bg-background min-h-[100dvh] overflow-y-auto">
        <div className="mb-6 sm:mb-8 lg:mb-12 shrink-0">
          <Link to="/login" className="inline-block">
            <img 
              src={logo} 
              alt="Postpally" 
              className="h-8 w-auto dark:hidden cursor-pointer hover:opacity-80 transition-opacity"
            />
            <img 
              src={logoWhite} 
              alt="Postpally" 
              className="h-8 w-auto hidden dark:block cursor-pointer hover:opacity-80 transition-opacity"
            />
          </Link>
        </div>
        <div className="w-full max-w-md px-1">{children}</div>
      </div>
    </div>
  );
};
